import type { OutboxItem } from '../types';

const DB_NAME = 'cyphex-chat';
const DB_VERSION = 1;
const STORE = 'outbox';

let handle: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (handle) return handle;
  handle = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'clientMsgId' }).createIndex('byConv', 'conversationId');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
  return handle;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return open().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        const request = work(db.transaction(STORE, mode).objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      }),
  );
}

/**
 * Durable send queue. Survives reload, tab crash and offline periods; the
 * `clientMsgId` primary key is also the server idempotency key, so replaying
 * the whole queue after a reconnect can never duplicate a message.
 */
export const outboxDb = {
  async all(): Promise<OutboxItem[]> {
    const rows = await run<OutboxItem[]>('readonly', (store) => store.getAll() as IDBRequest<OutboxItem[]>);
    return (rows ?? []).sort((a, b) => a.createdAt - b.createdAt);
  },
  async put(item: OutboxItem): Promise<void> {
    await run('readwrite', (store) => store.put(item) as IDBRequest<IDBValidKey>);
  },
  async remove(clientMsgId: string): Promise<void> {
    await run('readwrite', (store) => store.delete(clientMsgId) as IDBRequest<undefined>);
  },
  async clear(): Promise<void> {
    await run('readwrite', (store) => store.clear() as IDBRequest<undefined>);
  },
};
